import React, { JSX, useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import moment from 'moment';

import { Box, CircularProgress, IconButton, Paper, Typography } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { LatLng } from 'leaflet';

import { getCoreRowModel } from '@tanstack/react-table';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';

// Services
import { getVisitLog } from '../../../services/visit.service';
import AuthService from '../../../services/auth.service';

// Utils
import { Storage } from '../../../utils/Storage';
import { FormatForApi, shortDateFormat } from '../../../utils/Format';
import { makeVisitHistoryColumns } from '../../../utils/columns/makeVisitHistoryColumn';

// Types
import { Visit } from '../../../types/Visit';
import { VisitLogEntry } from '../../../types/VisitLogEntry';
import { FilterableTypes } from '../../../types/FilterableTypes';
import { SearchBarFilterTypes } from '../../../constants/Constants';
import { useVisitGeofences } from '../../../hooks/useVisitGeofences';

// Common
import SearchBar from '../../common/SearchBar/SearchBar';
import FormInputCollection from '../../common/FormInputCollection/FormInputCollection';
import { useStandardTable as useTable, Table } from '../../common/table/index';

// Sub-components
import AllSightingsMap from './AllSightingsMap';
import VisitJourneyPanel, { JourneyFacility } from './VisitJourneyMap/VisitJourneyPanel';
import VisitSightingsPanel from './VisitJourneyMap/VisitSightingsPanel';
import VisitJourneyMap, { UniqueFacility, UniqueReader } from './VisitJourneyMap/VisitJourneyMap';
import VisitHistoryOverview from './VisitHistoryOverview';
import StationToStationMap from './StationToStationMap';

const MAP_HEIGHT = 580;

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
  ...theme.typography.body2,
  padding: theme.spacing(1),
  marginBottom: 2,
  color: theme.palette.text.secondary,
}));

// Convert JourneyFacility[] (with locations) to VisitLogEntry[] shape so
// VisitJourneyMap can render one dot per facility on the left map.
// When a centroid lookup is provided, trail points snap to the geofence centroid.
const toMapEntries = (
  facilities: JourneyFacility[],
  visitId: string,
  centroidByFacilityId?: Map<string, { latitude: number; longitude: number }>,
): VisitLogEntry[] =>
  facilities
    .map((f) => {
      const centroid = centroidByFacilityId?.get(f.facilityId);
      const loc = centroid ?? f.location;
      if (loc?.latitude == null) return null;
      return {
        id: f.rowId ?? f.facilityId,
        deviceId: '',
        facilityId: f.facilityId,
        facilityName: f.facilityName,
        closestReaderImei: '',
        closestReaderName: f.facilityName,
        firstSeenDateTime: f.firstSeenDateTime,
        lastSeenDateTime: f.lastSeenDateTime,
        rssi: 0,
        temperatureF: f.averageTemperatureF,
        temperatureC: f.averageTemperatureC,
        humidity: 0,
        batteryPercent: 0,
        location: loc,
        locationMethodDescr: f.locationMethodDescr ?? '',
        locationMethod: f.locationMethod,
        visitId,
      } as VisitLogEntry;
    })
    .filter((e): e is VisitLogEntry => e !== null);

interface VisitHistoryProps {
  currentVisit?: Visit | null;
  // The visit shown in the Current tab's "Current Visit" card. Drives the Device
  // Overview header so both screens report the same visit, independently of what
  // the visit log table below happens to be paging through.
  currentVisitSummary?: Visit | null;
  onNavigateToCurrentTab?: () => void;
}

export default function VisitHistory({ currentVisit, currentVisitSummary, onNavigateToCurrentTab }: VisitHistoryProps): JSX.Element {
  const { id } = useParams<{ id: string }>();

  const [searchParams] = useSearchParams();
  const bySerial = searchParams.get('bySerial') === 'true';
  const filterField = bySerial ? 'SerialNumber' : 'DeviceId';

  // Visit table filters
  const [paramFilter, setParamFilter] = useState<any>({
    sortBy: [{ lastSeenDateTime: '-1' }],
    filters: id ? [{ [filterField]: id }] : [],
  });
  const [lastSeenDateTimeFrom, setLastSeenDateTimeFrom] = useState<Date | null>(null);
  const [lastSeenDateTimeTo, setLastSeenDateTimeTo] = useState<Date | null>(null);
  const [isResetting, setIsResetting] = useState(false);
  const [selectedVisitId, setSelectedVisitId] = useState<string | null>(null);

  const lastSeenDateTimeFromRef = useRef<Date | null>(null);
  const lastSeenDateTimeToRef = useRef<Date | null>(null);

  // Panel state
  const [detailVisitId, setDetailVisitId] = useState<string | null>(null);
  const [panelVisible, setPanelVisible] = useState(false); // journey panel slide

  const [showSightings, setShowSightings] = useState(false); // sightings layer mounted
  const [sightingsPanelVisible, setSightingsPanelVisible] = useState(false); // sightings slide
  const [selectedFacilityId, setSelectedFacilityId] = useState<string | null>(null);
  const [selectedFacilityFirstSeen, setSelectedFacilityFirstSeen] = useState<string | null>(null);
  const [selectedFacilityLastSeen, setSelectedFacilityLastSeen] = useState<string | null>(null);
  const [selectedFacilityLogId, setSelectedFacilityLogId] = useState<string | null>(null);

  // Journey-level map state
  const [journeyFacilities, setJourneyFacilities] = useState<JourneyFacility[]>([]);
  const [journeyActiveIdx, setJourneyActiveIdx] = useState<number | null>(null);
  const [journeyLoading, setJourneyLoading] = useState(false);

  // Sightings-level map state
  const [panelData, setPanelData] = useState<VisitLogEntry[]>([]);
  const [activeIdx, setActiveIdx] = useState<number | null>(null);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const [sightingsLoading, setSightingsLoading] = useState(true);
  const [sightingsError, setSightingsError] = useState(false);

  const isExternalUser = AuthService.getStoredUserProfile()?.isInternal === false;

  // Sync device id filter
  useEffect(() => {
    if (id) {
      setParamFilter((prev: any) => ({
        ...prev,
        filters: [{ [filterField]: id }],
        sortBy: prev?.sortBy ?? [{ lastSeenDateTime: '-1' }],
      }));
    }
  }, [id, filterField]);

  useEffect(() => {
    if (isResetting) {
      setParamFilter({ filters: [{ [filterField]: id }], sortBy: [{ lastSeenDateTime: '-1' }] });
      setIsResetting(false);
    }
  }, [isResetting, id]);

  useEffect(() => {
    setSelectedVisitId(null);
  }, [id]);

  // Panel handlers─

  // Visit table → "View Details" → open journey panel
  const handleViewDetails = (visitId: string) => {
    setDetailVisitId(visitId);

    setJourneyActiveIdx(null);
    setJourneyFacilities([]);

    setJourneyLoading(true);

    setShowSightings(false);
    setSightingsPanelVisible(false);

    setActiveIdx(null);
    setPanelData([]);
    requestAnimationFrame(() => setPanelVisible(true));
  };

  // Journey panel → back → close journey panel, return to table
  const handleBack = () => {
    setPanelVisible(false);
    setJourneyActiveIdx(null);
    setSelectedVisitId(null);
    setTimeout(() => {
      setDetailVisitId(null);
      setJourneyFacilities([]);
      setShowSightings(false);
    }, 300);
  };

  // Journey panel → "View Sightings" → open sightings panel over journey panel
  const handleViewSightings = (facilityId: string, firstSeenDateTime: string, lastSeenDateTime: string, closestFacilityLogId: string) => {
    if (isExternalUser) return;

    setSightingsLoading(true);
    setSightingsError(false);

    setSelectedFacilityId(facilityId);
    setSelectedFacilityFirstSeen(firstSeenDateTime);
    setSelectedFacilityLastSeen(lastSeenDateTime);
    setSelectedFacilityLogId(closestFacilityLogId || null);
    setActiveIdx(null);
    setPanelData([]);
    setShowSightings(true);
    requestAnimationFrame(() => setSightingsPanelVisible(true));
  };

  // Sightings panel → back → close sightings panel, return to journey panel
  const handleBackFromSightings = () => {
    setSightingsPanelVisible(false);
    setActiveIdx(null);
    setTimeout(() => {
      setShowSightings(false);
      setSelectedFacilityId(null);
      setSelectedFacilityFirstSeen(null);
      setSelectedFacilityLastSeen(null);
      setSelectedFacilityLogId(null);
      setPanelData([]);
    }, 300);
  };

  const handleMarkerClick = useCallback((i: number) => setActiveIdx(i), []);
  const handleHover = useCallback((i: number | null) => setHoveredIdx(i), []);
  const handleActiveIdxChange = useCallback((idx: number | null) => setActiveIdx(idx), []);
  const handleDataChange = useCallback((data: VisitLogEntry[]) => setPanelData(data), []);

  // Anchor the geofence fetch on the first journey facility that has a location.
  const geofenceAnchorLocation = useMemo(() => {
    const withLoc = journeyFacilities.find((f) => f.location?.latitude != null);
    return withLoc?.location ?? null;
  }, [journeyFacilities]);

  const { geofences, centroidByFacilityId } = useVisitGeofences({
    location: geofenceAnchorLocation,
    journeyFacilities,
  });

  // Journey map computations
  const journeyMapData = useMemo(
    () => toMapEntries(journeyFacilities, detailVisitId ?? '', centroidByFacilityId),
    [journeyFacilities, detailVisitId, centroidByFacilityId],
  );

  const journeyExactCoords = useMemo<[number, number][]>(
    () => journeyMapData.map((e) => [e.location.latitude, e.location.longitude]),
    [journeyMapData],
  );

  const journeyCenter = useMemo<LatLng>(
    () => (journeyMapData.length > 0 ? new LatLng(journeyMapData[0].location.latitude, journeyMapData[0].location.longitude) : new LatLng(0, 0)),
    [journeyMapData],
  );

  const journeyActivePosition = useMemo<LatLng | null>(() => {
    if (journeyActiveIdx === null || !journeyMapData[journeyActiveIdx]) return null;
    return new LatLng(journeyMapData[journeyActiveIdx].location.latitude, journeyMapData[journeyActiveIdx].location.longitude);
  }, [journeyActiveIdx, journeyMapData]);

  const journeyUniqueFacilities = useMemo<UniqueFacility[]>(
    () =>
      journeyMapData.map((e) => {
        const pad = 0.0008;
        const lat = e.location.latitude;
        const lng = e.location.longitude;
        return {
          lat,
          lng,
          name: e.facilityName ?? '',
          bounds: [
            [lat + pad, lng - pad],
            [lat + pad, lng + pad],
            [lat - pad, lng + pad],
            [lat - pad, lng - pad],
          ] as [number, number][],
        };
      }),
    [journeyMapData],
  );

  // Sightings map computations
  const exactCoords = useMemo<[number, number][]>(() => panelData.map((e) => [e.location.latitude, e.location.longitude]), [panelData]);

  const spreadPositions = useMemo<[number, number][]>(() => {
    const groups: Record<string, number[]> = {};
    panelData.forEach((e, i) => {
      const key = `${e.location.latitude.toFixed(6)},${e.location.longitude.toFixed(6)}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(i);
    });
    const result: [number, number][] = new Array(panelData.length);
    Object.values(groups).forEach((indices) => {
      const n = indices.length;
      indices.forEach((idx, j) => {
        const e = panelData[idx];
        if (n === 1) {
          result[idx] = [e.location.latitude, e.location.longitude];
        } else {
          const angle = (j / n) * 2 * Math.PI - Math.PI / 2;
          const r = 0.00015;
          result[idx] = [
            e.location.latitude + r * Math.cos(angle),
            e.location.longitude + (r * Math.sin(angle)) / Math.cos((e.location.latitude * Math.PI) / 180),
          ];
        }
      });
    });
    return result;
  }, [panelData]);

  const mapCenter = useMemo<LatLng>(
    () => (panelData.length > 0 ? new LatLng(panelData[0].location.latitude, panelData[0].location.longitude) : new LatLng(0, 0)),
    [panelData],
  );

  const activePosition = useMemo<LatLng | null>(() => {
    if (activeIdx === null || !panelData[activeIdx]) return null;
    return new LatLng(panelData[activeIdx].location.latitude, panelData[activeIdx].location.longitude);
  }, [activeIdx, panelData]);

  const uniqueReaders = useMemo<UniqueReader[]>(() => {
    const seen = new Map<string, UniqueReader>();
    panelData.forEach((e) => {
      const key = `${e.location.latitude.toFixed(6)},${e.location.longitude.toFixed(6)}`;
      if (!seen.has(key))
        seen.set(key, { lat: e.location.latitude, lng: e.location.longitude, name: e.closestReaderName, imei: e.closestReaderImei });
    });
    return Array.from(seen.values());
  }, [panelData]);

  const uniqueFacilities = useMemo<UniqueFacility[]>(() => {
    const seen = new Map<string, UniqueFacility>();
    panelData.forEach((e) => {
      if (!e.facilityName) return;
      if (!seen.has(e.facilityName)) {
        const lat = e.location.latitude;
        const lng = e.location.longitude;
        const pad = 0.0008;
        seen.set(e.facilityName, {
          lat,
          lng,
          name: e.facilityName,
          bounds: [
            [lat + pad, lng - pad],
            [lat + pad, lng + pad],
            [lat - pad, lng + pad],
            [lat - pad, lng - pad],
          ],
        });
      }
    });
    return Array.from(seen.values());
  }, [panelData]);

  const columns = useMemo(
    () => makeVisitHistoryColumns((visitId) => setSelectedVisitId((prev) => (prev === visitId ? null : visitId)), selectedVisitId, handleViewDetails),
    [selectedVisitId],
  );

  const table = useTable<Visit>({
    columns,
    fetchAsync: getVisitLog,
    params: paramFilter,
    serverSide: true,
    shouldFetch: true,
  });

  const handleLastSeenFromChange = (date: Date | null) => {
    lastSeenDateTimeFromRef.current = date;
    setLastSeenDateTimeFrom(date);
  };

  const handleLastSeenToChange = (date: Date | null) => {
    lastSeenDateTimeToRef.current = date;
    setLastSeenDateTimeTo(date);
  };

  const onSearchChange = (filters: any[]) => {
    const finalFilters = [...filters];

    if (id) {
      const hasDeviceFilter = finalFilters.some((f) => filterField in f);
      if (!hasDeviceFilter) {
        finalFilters.unshift({ [filterField]: id });
      }
    }

    const from = lastSeenDateTimeFromRef.current;
    const to = lastSeenDateTimeToRef.current;

    if (from && to) {
      finalFilters.push({
        lastSeenDateTime: FormatForApi(moment(from), true),
        filterType: FilterableTypes.GreaterThanOrEqual,
        and: { lastSeenDateTime: FormatForApi(moment(to), false), filterType: FilterableTypes.LessThanOrEqual },
      });
    }

    setParamFilter((prev: any) => ({
      ...prev,
      filters: finalFilters,
      sortBy: prev.sortBy ?? [{ lastSeenDateTime: '-1' }],
    }));

    Storage.setSearchBarFiltering(SearchBarFilterTypes.VISIT_HISTORY, { isMoreFilter: true, filters: finalFilters });
  };

  const handleReset = () => {
    setSelectedVisitId(null);
    setIsResetting(true);

    lastSeenDateTimeFromRef.current = null;
    lastSeenDateTimeToRef.current = null;
    setLastSeenDateTimeFrom(null);
    setLastSeenDateTimeTo(null);
  };

  return (
    <>
      <VisitHistoryOverview currentVisit={currentVisitSummary ?? currentVisit ?? null} />

      <SearchBar
        pageKey={SearchBarFilterTypes.VISIT_HISTORY}
        columns={columns}
        onSearch={onSearchChange}
        allowSearchAll
        onReset={handleReset}
        components={
          <div>
            <div style={{ display: 'flex', flexWrap: 'wrap', marginBottom: 5, paddingLeft: 15 }}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <FormInputCollection label='Last Seen date'>
                  <div style={{ marginRight: '8px' }}>
                    <DatePicker
                      value={lastSeenDateTimeFrom ? moment(lastSeenDateTimeFrom) : null}
                      label='From'
                      format={shortDateFormat}
                      sx={{ width: '185px' }}
                      slotProps={{ textField: { size: 'small' } }}
                      onChange={(date) => handleLastSeenFromChange(date ? date.toDate() : null)}
                    />
                  </div>
                  <DatePicker
                    value={lastSeenDateTimeTo ? moment(lastSeenDateTimeTo) : null}
                    label='To'
                    format={shortDateFormat}
                    sx={{ width: '185px' }}
                    slotProps={{ textField: { size: 'small' } }}
                    onChange={(date) => handleLastSeenToChange(date ? date.toDate() : null)}
                  />
                </FormInputCollection>
              </LocalizationProvider>
            </div>
          </div>
        }
      />

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 1, mt: 1, alignItems: 'flex-start' }}>
        {/* Left: map */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {detailVisitId && showSightings ? (
            // Sightings panel open → show individual sightings on map
            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', mt: 1 }}>
              <Box
                sx={{
                  px: 2,
                  py: 1.25,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.default',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Typography variant='subtitle2' fontWeight={600}>
                  Visit Sightings
                </Typography>
              </Box>
              <Box sx={{ height: MAP_HEIGHT, position: 'relative' }}>
                {sightingsLoading ? (
                  <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1 }}>
                    <CircularProgress size={28} sx={{ color: '#7c3aed' }} />
                    <Typography variant='caption' color='text.secondary'>
                      Loading sightings...
                    </Typography>
                  </Box>
                ) : panelData.length === 0 ? (
                  <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant='body2' color='text.secondary'>
                      {sightingsError ? 'No data to display. Please try again later.' : 'No data to display.'}
                    </Typography>
                  </Box>
                ) : (
                  <VisitJourneyMap
                    filtered={panelData}
                    exactCoords={exactCoords}
                    spreadPositions={spreadPositions}
                    center={mapCenter}
                    activePosition={activePosition}
                    activeIdx={activeIdx}
                    hoveredIdx={hoveredIdx}
                    uniqueReaders={uniqueReaders}
                    uniqueFacilities={uniqueFacilities}
                    geofences={geofences}
                    onMarkerClick={handleMarkerClick}
                    onMarkerHover={handleHover}
                  />
                )}
              </Box>
            </Paper>
          ) : detailVisitId ? (
            // Journey panel open → show one point per facility on map
            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', mt: 1 }}>
              <Box
                sx={{
                  px: 2,
                  py: 1.25,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.default',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Typography variant='subtitle2' fontWeight={600}>
                  Visit Journey
                </Typography>
              </Box>
              <Box sx={{ height: MAP_HEIGHT, position: 'relative' }}>
                {journeyLoading ? (
                  <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1 }}>
                    <CircularProgress size={28} sx={{ color: '#7c3aed' }} />
                    <Typography variant='caption' color='text.secondary'>
                      Loading journey...
                    </Typography>
                  </Box>
                ) : journeyMapData.length === 0 ? (
                  <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1 }}>
                    <Typography variant='body2' color='text.secondary'>
                      No mappable location for this journey.
                    </Typography>
                  </Box>
                ) : (
                  <VisitJourneyMap
                    filtered={journeyMapData}
                    exactCoords={journeyExactCoords}
                    spreadPositions={journeyExactCoords}
                    center={journeyCenter}
                    activePosition={journeyActivePosition}
                    activeIdx={journeyActiveIdx}
                    hoveredIdx={null}
                    uniqueReaders={[]}
                    uniqueFacilities={journeyUniqueFacilities}
                    geofences={geofences}
                    onMarkerClick={setJourneyActiveIdx}
                    onMarkerHover={() => void 0}
                  />
                )}
              </Box>
            </Paper>
          ) : selectedVisitId ? (
            // Eye icon selected → single-visit journey map
            <AllSightingsMap deviceId={id ?? ''} visitId={selectedVisitId} />
          ) : (
            // Default → station-to-station journey map
            <StationToStationMap
              deviceId={id ?? ''}
              visits={table.data}
              activeVisit={currentVisit}
              activeVisitId={currentVisit?.visitId}
              onStationClick={handleViewDetails}
              onActiveStationClick={onNavigateToCurrentTab}
            />
          )}
        </Box>

        {/* Right: visit table + slide-over panels */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            position: 'relative',
            overflow: 'hidden',
            height: MAP_HEIGHT + 40,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Base: visit history table */}
          <Item
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              p: 0,
              m: 0,
              overflow: 'hidden',
              '& .ui-table': {
                height: '100%',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
              },
              '& .ui-table__card': {
                flex: '1 1 auto',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
              },
              '& .ui-table__scroll': {
                flex: '1 1 auto',
                minHeight: 0,
                maxHeight: 'none !important',
                overflowY: 'auto !important',
              },
              '& .ui-table__pagination': {
                flex: '0 0 auto',
              },
            }}
          >
            <Table
              {...table}
              showSearchBar={false}
              showColumnToggler={false}
              onReload={() => setSelectedVisitId(null)}
              getCoreRowModel={getCoreRowModel()}
              initialState={{
                sorting: [
                  {
                    id: 'lastSeenDateTime',
                    desc: true,
                  },
                ],
              }}
            />
          </Item>

          {/* Layer 2: Journey panel — slides over table */}
          {detailVisitId && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                boxSizing: 'border-box',
                border: '2px solid',
                borderColor: '#dfe6ef',
                borderRadius: 4,
                bgcolor: 'background.paper',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                transform: panelVisible ? 'translateX(0)' : 'translateX(100%)',
                transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                zIndex: 10,
                boxShadow: '-4px 0 20px rgba(15, 23, 42, 0.12)',
              }}
            >
              {/* Journey panel header */}
              <Box
                sx={{
                  px: 2,
                  py: 1.25,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  bgcolor: '#f8fafc',
                  flexShrink: 0,
                }}
              >
                <IconButton size='small' onClick={handleBack} sx={{ color: '#334155' }}>
                  <ArrowBackIcon fontSize='small' />
                </IconButton>
                <Typography variant='subtitle2' fontWeight={700} sx={{ color: '#111827' }}>
                  Visit Journey
                </Typography>
                <Typography variant='caption' color='text.secondary' sx={{ ml: 0.5 }}>
                  {detailVisitId}
                </Typography>
              </Box>

              <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <VisitJourneyPanel
                  visitId={detailVisitId}
                  activeIdx={journeyActiveIdx}
                  canViewSightings={!isExternalUser}
                  showRaisedStats={!showSightings}
                  raisedStatsTop={0}
                  raisedStatsBottomMargin={20}
                  tableLeftMargin={0}
                  onViewSightings={handleViewSightings}
                  onFacilitiesChange={setJourneyFacilities}
                  onFocusMarker={setJourneyActiveIdx}
                  onLoadingChange={setJourneyLoading}
                  onErrorChange={() => void 0}
                />
              </Box>

              {/* Layer 3: Sightings panel — slides over journey panel */}
              {showSightings && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    boxSizing: 'border-box',
                    border: '2px solid',
                    borderColor: '#dfe6ef',
                    borderRadius: 4,
                    bgcolor: 'background.paper',
                    overflow: 'hidden',
                    zIndex: 20,
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      boxSizing: 'border-box',
                      bgcolor: 'background.paper',
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden',
                      transform: sightingsPanelVisible ? 'translateX(0)' : 'translateX(100%)',
                      transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      zIndex: 20,
                      boxShadow: '-4px 0 20px rgba(15, 23, 42, 0.12)',
                    }}
                  >
                    {/* Sightings panel header */}
                    <Box
                      sx={{
                        px: 2,
                        py: 1.25,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        bgcolor: '#f8fafc',
                        flexShrink: 0,
                      }}
                    >
                      <IconButton size='small' onClick={handleBackFromSightings} sx={{ color: '#334155' }}>
                        <ArrowBackIcon fontSize='small' />
                      </IconButton>
                      <Typography variant='subtitle2' fontWeight={700} sx={{ color: '#111827' }}>
                        Visit Sightings
                      </Typography>
                      <Typography variant='caption' color='text.secondary' sx={{ ml: 0.5 }}>
                        {detailVisitId}
                      </Typography>
                    </Box>

                    <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                      <VisitSightingsPanel
                        visitId={detailVisitId}
                        facilityId={selectedFacilityId}
                        closestFacilityLogId={selectedFacilityLogId}
                        firstSeenDateTime={selectedFacilityFirstSeen}
                        lastSeenDateTime={selectedFacilityLastSeen}
                        activeIdx={activeIdx}
                        onActiveIdxChange={handleActiveIdxChange}
                        onDataChange={handleDataChange}
                        onLoadingChange={setSightingsLoading}
                        onErrorChange={setSightingsError}
                      />
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
}
